import { useState } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import {
  Grid,
  Paper,
  Typography,
  Button,
  Box,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Alert,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { parseISO } from "date-fns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

import { createZoomMeeting, createTeamsMeeting } from "../../api/meetings.js";
import { useSelector } from "react-redux";
import { teamsLogin } from "../../api/auth.js";
import { handleAxiosError } from "../../config/action-config.js";
import UserListSelector from "../../components/UserListSelector.jsx";
import useSnackbar from "../../hooks/useSnackbar.js";
import useErrorHandler from "../../hooks/useErrorHandler.js";

export default function NewMeeting() {
  const { data: user } = useSelector((state) => state.user);

  const [meetingProvider, setMeetingProvider] = useState("ZOOM");

  return (
    <>
      <Paper className="odr-card ">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          className="odr-card-header"
        >
          <FormControl
            required
            sx={{ width: 250 }}
            size="small"
            varaint="outlined"
          >
            <InputLabel id="demo-simple-select-mp">Meeting Provider</InputLabel>
            <Select
              variant="outlined"
              labelId="demo-simple-select-label-mp"
              id="demo-simple-select-mp"
              name="timezone"
              value={meetingProvider}
              label="Meeting Provider"
              onChange={({ target }) => setMeetingProvider(target.value)}
            >
              <MenuItem value="ZOOM">Zoom</MenuItem>
              {/* <MenuItem value="MS-TEAMS">MS Teams</MenuItem> */}
            </Select>
          </FormControl>
          <Link to="/cases">
            <Button
              variant="outlined"
              size="large"
              className="black-boder-button"
            >
              Cancel
            </Button>
          </Link>
        </Box>

        <ProviderSwitch
          user={user}
          provider={meetingProvider}
          teamsToken={user.msTeamsAuth}
        />
      </Paper>
    </>
  );
}

function ProviderSwitch({ provider, user, teamsToken }) {
  if (provider === "ZOOM") return <ZoomMeeting token={user.token} />;

  return (
    <TeamsMeeting
      token={user.token}
      authenticated={Boolean(user.msTeamsAuth)}
      teamsToken={teamsToken}
    />
  );
}

const emptyZoomMeetingData = {
  schedule_for: "suman@vastedge.com",
  agenda: "",
  topic: "",
  password: "",
  duration: 60,
  start_time: "",
  // "timezone": "",
  //   "description": "",
  //   "default_password": false,
  //   "pre_schedule": false,
  // "schedule_for": "vik@vastedge.com", // should be user.email
  //   "template_id": "Dv4YdINdTk+Z5RToadh5ug==",
  //    "type": 2
};

function ZoomMeeting({ token }) {
  const params = useParams();
  const navigate = useNavigate();
  const errorHandler = useErrorHandler();
  const snackbar = useSnackbar();
  const { data: user } = useSelector((state) => state.user);
  const [data, setData] = useState(emptyZoomMeetingData);
  const onInputChange = ({ target }) => {
    setData((prev) => ({ ...prev, [target.name]: target.value }));
  };

  const [meetingAttendees, setMeetingAttendees] = useState([]);
  function collectAttendees(list) {
    setMeetingAttendees(list.map((a) => ({ id: a.id, email: a.email })));
  }

  const onCreateFormSubmit = async (e) => {
    e.preventDefault();
    data.topic = data.agenda;
    try {
      await createZoomMeeting(
        { ...data, attendees: meetingAttendees, case_id: params.id },
        token
      );
      snackbar.showSuccess("Meeting Scheduled.");
      navigate(`/cases/${params.id}`);
    } catch (err) {
      errorHandler(err);
    }
  };

  return (
    <Box px={3} pb={3} component="form" onSubmit={onCreateFormSubmit}>
      <Grid
        container
        rowSpacing={2}
        columnSpacing={{ xs: 1, sm: 1, md: 1 }}
        sx={{
          display: "flex",
          //justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Grid item xs={12} sm={12} md={12} lg={12}>
          <Typography className="heading-title">
            Zoom Meeting Details
          </Typography>
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <TextField
            value={data.agenda}
            label="Agenda"
            onChange={onInputChange}
            size="small"
            fullWidth
            variant="outlined"
            name="agenda"
            required
          />
        </Grid>
        {/* <Grid item xs={12} md={6} lg={8}>
                    <TextField value={data.description} label="Description" onChange={onInputChange}
                        size='small' fullWidth variant="outlined" name="description" required
                    />
                </Grid> */}
        {/* <Grid item xs={12} md={6} lg={4}>
                    <TextField value={data.topic} label="Topic" onChange={onInputChange}
                        size='small' fullWidth variant="outlined" name="topic" required
                    />
                </Grid> */}
        <Grid item xs={12} md={6} lg={4}>
          <TextField
            value={data.password}
            type="password"
            label="Password"
            onChange={onInputChange}
            size="small"
            fullWidth
            variant="outlined"
            name="password"
            required
          />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="Start Time"
              required
              minDateTime={parseISO(new Date().toISOString())}
              value={parseISO(data.start_time)}
              onChange={(newValue) => {
                onInputChange({
                  target: {
                    name: "start_time",
                    value: newValue.toISOString(),
                  },
                });
              }}
              renderInput={(params) => (
                <TextField
                  required
                  {...params}
                  fullWidth
                  size="small"
                  variant="outlined"
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <TextField
            value={data.duration}
            type="number"
            label="Duration (Minute)"
            onChange={onInputChange}
            size="small"
            fullWidth
            variant="outlined"
            name="duration"
            required
            InputProps={{ inputProps: { max: 100, min: 10 } }}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <UserListSelector label="Attendees" collectData={collectAttendees} />
        </Grid>

        <Grid item xs={12} md="auto" lg="auto">
          <Button
            disabled={/^trial_user$/i.test(user.roles) ? true : false}
            variant="contained"
            color="primary"
            type="submit"
            size="large"
            className="blue-button"
          >
            Create
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

const emptyTeamMeetingData = {
  subject: "",
  start: "",
  end: "",
};

function TeamsMeeting({ token, authenticated, teamsToken }) {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const errorHandler = useErrorHandler();
  const [data, setData] = useState(emptyTeamMeetingData);
  const [meetingAttendees, setMeetingAttendees] = useState([]);

  const onLinkAccountClick = async () => {
    try {
      const { data } = await teamsLogin(token);
      window.location.replace(data.link);
    } catch (error) {
      errorHandler(error);
    }
  };

  if (!authenticated)
    return (
      <Paper elevation={3}>
        <Box p={1} textAlign="center">
          <Alert severity="info">
            You need to link your Microsoft Teams Account to create Teams
            Meeting.
          </Alert>

          <Button
            sx={{ mt: 1 }}
            variant="contained"
            onClick={onLinkAccountClick}
            color="secondary"
          >
            Link Account
          </Button>
        </Box>
      </Paper>
    );

  const onInputChange = ({ target }) => {
    setData((prev) => ({ ...prev, [target.name]: target.value }));
  };

  function collectAttendees(list) {
    setMeetingAttendees(list.map((a) => ({ id: a.id, email: a.email })));
  }

  const onCreateFormSubmit = async (e) => {
    e.preventDefault();

    try {
      const { error } = await createTeamsMeeting(
        { ...data, attendees: meetingAttendees },
        token,
        teamsToken
      );
      if (error) throw error;

      snackbar.showSuccess("Meeting Scheduled.");
      navigate("/meetings");
    } catch (err) {
      errorHandler(err);
    }
  };

  return (
    <Paper elevation={3}>
      <Box p={2}>
        <Typography>Teams Meeting Details</Typography>
      </Box>

      <Box px={2} pb={2} component="form" onSubmit={onCreateFormSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={4}>
            <TextField
              value={data.subject}
              label="Subject"
              onChange={onInputChange}
              size="small"
              fullWidth
              variant="outlined"
              name="subject"
              required
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label="Start Time"
                required
                minDateTime={parseISO(new Date().toISOString())}
                value={parseISO(data.start)}
                onChange={(newValue) => {
                  onInputChange({
                    target: { name: "start", value: newValue.toISOString() },
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    required
                    {...params}
                    fullWidth
                    size="small"
                    variant="outlined"
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label="End Time"
                required
                minDateTime={parseISO(new Date().toISOString())}
                value={parseISO(data.end)}
                onChange={(newValue) => {
                  onInputChange({
                    target: { name: "end", value: newValue.toISOString() },
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    required
                    {...params}
                    fullWidth
                    size="small"
                    variant="outlined"
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <UserListSelector
              label="Attendees"
              collectData={collectAttendees}
            />
          </Grid>
          <Grid item xs={12} md="auto" lg="auto">
            <Button
              variant="contained"
              color="primary"
              type="submit"
              className="blue-button"
              size="large"
            >
              Create
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}
